import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DeleteCategory } from "../../modal_page/deleteCategory";
import { EditCategoryRedux } from "../../../../reducer/category_reducer";
import { AllCategory, getAllCatagory } from "../../../../api/category.api";

interface Props {
  /** Filter to one channel. Omit to show every category. */
  channel?: "cafe" | "bar" | "dessert";
  title?: string;
  subtitle?: string;
}

type ChannelKey = "cafe" | "bar" | "dessert";
type FilterKey = "all" | ChannelKey;

const CHANNEL_LABEL: Record<ChannelKey, string> = {
  cafe: "Café",
  bar: "Bar",
  dessert: "Dessert",
};

const CategoryManagement: React.FC<Props> = ({ channel, title, subtitle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [categories, setCategories] = useState<AllCategory>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");

  const fetchCategory = () => {
    setIsLoading(true);
    getAllCatagory().then((all) => {
      const filtered = all.filter(
        (c) => c.isAvailable === "available" && (!channel || c.categoryFor === channel),
      );
      setCategories(filtered);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const counts = useMemo(() => {
    const c = { all: categories.length, cafe: 0, bar: 0, dessert: 0 };
    for (const cat of categories) c[cat.categoryFor] += 1;
    return c;
  }, [categories]);

  const totalItems = useMemo(
    () =>
      categories.reduce(
        (sum, c) => sum + c.menus.filter((m) => m.isAvailable === "available").length,
        0,
      ),
    [categories],
  );

  const visible = useMemo(
    () => (filter === "all" ? categories : categories.filter((c) => c.categoryFor === filter)),
    [categories, filter],
  );

  const handleOpenCategory = (c: AllCategory[number]) => {
    dispatch({
      type: "EDITCATEGORY",
      payload: {
        category_name: c.name,
        category_id: c.id,
        category_for: c.categoryFor,
        isAvailable: c.isAvailable,
      } as EditCategoryRedux,
    });
    navigate(`/menuManagement/${c.name}/${c.id}`);
  };

  const handleEdit = (c: AllCategory[number]) => {
    dispatch({
      type: "EDITCATEGORY",
      payload: {
        category_name: c.name,
        category_id: c.id,
        category_for: c.categoryFor,
        isAvailable: c.isAvailable,
      } as EditCategoryRedux,
    });
    navigate(`/edit-category/${c.id}`);
  };

  const handleDelete = (c: AllCategory[number]) => {
    dispatch({
      type: "EDITCATEGORY",
      payload: {
        category_name: c.name,
        category_id: c.id,
        category_for: c.categoryFor,
        isAvailable: "unavailable",
      } as EditCategoryRedux,
    });
    setShowDelete(true);
  };

  const showFilters = !channel;

  return (
    <div className="min-h-screen bg-white font-sans pb-[120px]">
      <header className="px-5 md:px-10 pt-[28px] pb-[20px]">
        <div className="max-w-[1100px] mx-auto">
          <p className="eyebrow-v2 text-ink-500">Menu</p>
          <div className="flex items-end justify-between gap-4 mt-1">
            <h1 className="font-display font-black text-[34px] md:text-[44px] leading-[1.05] tracking-tightest text-ink-1000">
              {title ?? "Management"}
            </h1>
            <button
              type="button"
              onClick={() => navigate("/add-category")}
              className="hidden md:inline-flex btn-v2-primary btn-v2-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New category
            </button>
          </div>
          <p className="text-sm text-ink-500 mt-2">
            {subtitle ?? (
              <>
                <span className="text-ink-1000 font-semibold">{counts.all}</span> categories
                <span className="mx-1.5 text-ink-300">·</span>
                <span className="text-ink-1000 font-semibold">{totalItems}</span> items
              </>
            )}
          </p>
        </div>
      </header>

      {showFilters && (
        <div className="px-5 md:px-10 pt-1 pb-3">
          <div className="max-w-[1100px] mx-auto overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-2">
              {(["all", "cafe", "bar", "dessert"] as FilterKey[]).map((k) => {
                const active = filter === k;
                const label = k === "all" ? "All" : CHANNEL_LABEL[k];
                const count = counts[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setFilter(k)}
                    className={active ? "chip-v2-active" : "chip-v2"}
                  >
                    <span>{label}</span>
                    <span
                      className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-bold ${
                        active ? "bg-white/20 text-current" : "bg-ink-100 text-ink-700"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile primary action — keeps the New category CTA reachable on phones */}
      <div className="px-5 pt-1 pb-3 md:hidden">
        <button
          type="button"
          onClick={() => navigate("/add-category")}
          className="btn-v2-primary btn-v2-md w-full"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New category
        </button>
      </div>

      <main className="px-5 md:px-10">
        <div className="max-w-[1100px] mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card-v2 p-5 animate-pulse">
                  <div className="h-4 w-16 bg-ink-100 rounded-full mb-5" />
                  <div className="h-6 bg-ink-200 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-ink-100 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="py-[72px] flex flex-col items-center justify-center text-center">
              <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </div>
              <p className="font-display font-black text-lg text-ink-1000">
                {filter === "all" ? "No categories yet" : `No ${CHANNEL_LABEL[filter as ChannelKey]} categories`}
              </p>
              <p className="text-sm text-ink-500 mt-1">
                {filter === "all" ? 'Tap "New category" to add one' : "Try another filter or create one"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visible.map((c) => {
                const itemCount = c.menus.filter((m) => m.isAvailable === "available").length;
                return (
                  <div
                    key={c.id}
                    className="card-v2 relative group transition-all duration-150 hover:shadow-card hover:-translate-y-px"
                  >
                    <button
                      type="button"
                      onClick={() => handleOpenCategory(c)}
                      className="block text-left w-full p-5 pr-[88px]"
                      aria-label={`Open ${c.name}`}
                    >
                      <span className="inline-flex items-center gap-1.5 px-2 h-[22px] rounded-full bg-ink-100 text-ink-700 text-[10px] font-bold uppercase tracking-[0.08em]">
                        <span className="w-1 h-1 rounded-full bg-ink-500" />
                        {CHANNEL_LABEL[c.categoryFor]}
                      </span>
                      <div className="font-display font-black text-[22px] leading-[1.1] tracking-tight text-ink-1000 mt-3 line-clamp-2">
                        {c.name}
                      </div>
                      <div className="text-xs text-ink-500 mt-2 font-medium">
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </div>
                    </button>

                    <div className="absolute top-3 right-3 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleEdit(c); }}
                        aria-label="Edit category"
                        className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-ink-700 hover:bg-ink-100 active:scale-95 transition"
                      >
                        <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                        aria-label="Delete category"
                        className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-red-600 hover:bg-red-50 active:scale-95 transition"
                      >
                        <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Add-new tile — keeps the create CTA in the user's flow */}
              <button
                type="button"
                onClick={() => navigate("/add-category")}
                className="hidden md:flex relative items-center justify-center min-h-[148px] rounded-2xl border-2 border-dashed border-ink-200 text-ink-500 hover:border-ink-1000 hover:text-ink-1000 transition-colors group"
              >
                <span className="flex flex-col items-center gap-2">
                  <span className="w-[40px] h-[40px] rounded-full bg-ink-100 group-hover:bg-ink-1000 group-hover:text-white flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </span>
                  <span className="font-semibold text-sm">New category</span>
                </span>
              </button>
            </div>
          )}
        </div>
      </main>

      <DeleteCategory
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onSuccess={fetchCategory}
      />
    </div>
  );
};

export default CategoryManagement;
